import { Stack } from "@mantine/core";
import { isNotEmpty, useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import FormModal from "components/FormModal";
import FeatureUploadImages from "components/ImageUploader/FeatureUploadImages";
import SendMessageForm from "components/SendMessageForm";
import React, { ReactElement, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ApplicationMessageForm } from "types/ApplicationMessage";
import { DocumentWithId, createDocumentWithId } from "types/document";

interface FeatureSendEmailFormValues {
  title: string;
  message: string;
  attachments: DocumentWithId[];
}

interface FeatureSendEmailProps {
  title: ReactNode;
  onSend: (message: ApplicationMessageForm) => Promise<void>;
  isPending: boolean;
  trigger: ReactElement<{ onClick?: () => void }>;
  details?: ReactNode;
}

const FeatureSendEmail: React.FC<FeatureSendEmailProps> = ({
  title,
  onSend,
  isPending,
  trigger,
  details,
}) => {
  const { t } = useTranslation("message");
  const [opened, { toggle }] = useDisclosure();
  const form = useForm<FeatureSendEmailFormValues>({
    initialValues: {
      title: "",
      message: "",
      attachments: [],
    },
    validate: {
      message: isNotEmpty(),
      title: isNotEmpty(),
    },
  });

  const appendAttachments = (document: Blob) => {
    const documentWithId = createDocumentWithId(document);
    form.insertListItem("attachments", documentWithId);
  };

  const removeAttachment = (id: string) => {
    const index = form.values.attachments.findIndex(doc => doc.id === id);
    if (index !== -1) {
      form.removeListItem("attachments", index);
    }
  };

  const handleClose = () => {
    form.reset();
    toggle();
  };

  const handleSubmit = async (values: FeatureSendEmailFormValues) => {
    try {
      const applicationMessage: ApplicationMessageForm = {
        title: values.title,
        message: values.message,
        attachments: values.attachments.map(doc => doc.blob),
      };

      await onSend(applicationMessage);
      handleClose();
    } catch {
      // onSend handles error feedback; keep modal open
    }
  };

  return (
    <>
      {React.cloneElement(trigger, { onClick: toggle })}

      {opened && (
        <FormModal
          xOffset={0}
          gap={20}
          onSubmit={form.onSubmit(handleSubmit)}
          title={title}
          opened={opened}
          onClose={handleClose}
          saveButtonLabel={t("send").toLocaleUpperCase()}
          isPending={isPending}
        >
          <Stack px={20}>
            {details}
            <SendMessageForm form={form}>
              <div className="mt-4">
                <FeatureUploadImages
                  documents={form.values.attachments}
                  onImageUpload={appendAttachments}
                  onRemoveImage={removeAttachment}
                />
              </div>
            </SendMessageForm>
          </Stack>
        </FormModal>
      )}
    </>
  );
};

export default FeatureSendEmail;
